import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { ApiError } from "../../api/errors";
import {
  canChangePrice,
  changePendingPrice,
  describeSelector,
  exportPriceList,
  isPendingChange,
  isRevertibleChange,
  revertPriceChange,
  istDay,
  listPriceComponents,
  listPriceRules,
  netOfGross,
  listSellerGst,
  priceWords,
  sellerGross,
  sellerGstOf,
  sellerWarningOf,
  speaksGst,
  warningWords,
  priceListRows,
  repricePriceRule,
  repriceProblems,
  rulePays,
  selectorOrder,
  sourceOf,
  type PendingChangeResult,
  type PriceListExport,
  type RevertResult,
  type PriceRule,
  type RepriceResult,
} from "../../api/priceList";
import { canOpenCreatePlan } from "../../api/planBuilder";
import { MARKDOWN, canKeepPriceListDocs, savePriceListDoc, textToBase64 } from "../../api/priceListDocs";

const inr = (n: number) => `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** An instant as its calendar date in India, dd-mm-yyyy. */
function istDate(s?: string): string {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", day: "2-digit", month: "2-digit", year: "numeric" }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("day")}-${get("month")}-${get("year")}`;
}

function errorTitle(e: Error): string {
  if (e instanceof ApiError) {
    switch (e.kind) {
      case "unauthenticated":
        return "Your sign-in has expired -- sign in again";
      case "forbidden":
        return "Refused -- this namespace is outside yours, or your role may not read price lists";
      case "not-found":
        return "The gateway does not publish the price list yet";
      default:
        break;
    }
  }
  return "Could not load the price list";
}

function RuleLine({ r }: { r: PriceRule }) {
  const until = r.effectiveto ? ` to ${istDate(r.effectiveto)}` : "";
  return (
    <Typography variant="caption" component="div" color={r.active ? "text.primary" : "text.disabled"}>
      {r.pricingmode} {r.pricingmode === "PERCENT_OF_PARENT" ? `${r.amount}%` : inr(r.amount)} · from {istDate(r.effectivefrom)}
      {until} · v{r.version}
      {r.active ? "" : " · withdrawn"}
    </Typography>
  );
}

/**
 * A charge's rules as a table, one row per plan it applies to (owner
 * 2026-10-11: sixteen "ABSOLUTE ₹254.24 · from 11-10-2026 · v1" lines said
 * nothing about which plan pays what).
 */
function RuleTable({ rules, gst, speak = true, onChange, onPending, onRevert }: {
  rules: PriceRule[];
  gst: number | undefined;
  /** false: an unregistered or composition operator -- no word of GST (owner 2026-10-29). */
  speak?: boolean;
  onChange?: (r: PriceRule) => void;
  /** A change not started yet: move its date or cancel it (owner 2026-10-28). */
  onPending?: (r: PriceRule, action: "move" | "cancel") => void;
  /** A change that has started: undo it from a date (owner 2026-10-29). */
  onRevert?: (r: PriceRule) => void;
}) {
  const now = new Date();
  const which = (r: PriceRule) => r.parentlabelselector ?? r.labelselector;
  const sorted = [...rules].sort((a, b) => selectorOrder(which(a), which(b)));
  const froms = new Set(rules.map((r) => istDate(r.effectivefrom)));
  return (
    <Table size="small" aria-label="Price by plan" sx={{ "& td, & th": { py: 0.25, px: 1, fontSize: 12 } }}>
      <TableHead>
        <TableRow>
          <TableCell>{rules.some((r) => r.parentlabelselector) ? "With plan" : "For"}</TableCell>
          <TableCell>Pays</TableCell>
          {froms.size > 1 && <TableCell>From</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {sorted.map((r) => (
          <TableRow key={r.id} sx={r.active ? undefined : { opacity: 0.55 }}>
            <TableCell>{describeSelector(which(r))}</TableCell>
            <TableCell>
              {rulePays(r, gst, speak)}
              {r.active ? "" : " · withdrawn"}
              {r.effectiveto ? ` · until ${istDate(r.effectiveto)}` : ""}
              {onPending && isPendingChange(r, now) ? (
                <>
                  {" · starts later"}
                  <Button size="small" sx={{ ml: 1, minWidth: 0, p: 0, fontSize: 11 }} onClick={() => onPending(r, "move")}>
                    Move date
                  </Button>
                  <Button size="small" sx={{ ml: 1, minWidth: 0, p: 0, fontSize: 11 }} onClick={() => onPending(r, "cancel")}>
                    Cancel change
                  </Button>
                </>
              ) : (
                <>
                  {onChange && canChangePrice(r) && (
                    <Button size="small" sx={{ ml: 1, minWidth: 0, p: 0, fontSize: 11 }} onClick={() => onChange(r)}>
                      Change price
                    </Button>
                  )}
                  {onRevert && isRevertibleChange(r, now) && (
                    <Button size="small" color="warning" sx={{ ml: 1, minWidth: 0, p: 0, fontSize: 11 }} onClick={() => onRevert(r)}>
                      Revert
                    </Button>
                  )}
                </>
              )}
            </TableCell>
            {froms.size > 1 && <TableCell>{istDate(r.effectivefrom)}</TableCell>}
          </TableRow>
        ))}
      </TableBody>
      {froms.size === 1 && (
        <caption style={{ captionSide: "bottom", textAlign: "left", fontSize: 11, padding: "2px 8px" }}>
          from {[...froms][0]}
        </caption>
      )}
    </Table>
  );
}

/** What a change did, in words. */
function repriceWords(res: RepriceResult, gst: number, speak = true): string {
  if (res.unchanged) return "That is already the price. Nothing changed.";
  const from = res.created ? istDate(res.created.effectivefrom) : "";
  const pays = res.created ? rulePays({ pricingmode: "ABSOLUTE", amount: res.created.amount }, gst, speak) : "";
  if (res.replay) return `This change was already made: ${pays} from ${from}. Nothing changed again.`;
  return `Done: ${pays} from ${from}. The old price applies until then.`;
}

function pendingWords(res: PendingChangeResult, action: "move" | "cancel", gst: number, speak = true): string {
  if (action === "cancel") {
    const old = res.predecessor ? rulePays({ pricingmode: "ABSOLUTE", amount: res.predecessor.amount }, gst, speak) : "the old price";
    return res.replay ? `This change was already cancelled. ${old} stays.` : `Cancelled. ${old} stays, with no end date.`;
  }
  const from = res.created ? istDate(res.created.effectivefrom) : "";
  return res.replay ? `This move was already made: the new price starts ${from}.` : `Moved: the new price starts ${from}; the old one applies until then.`;
}

function saveText(text: string, filename: string) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type: "text/markdown" }));
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

/**
 * Download current price list (owner 2026-10-28, price-list idempotency step
 * 2): the prices billing charges today, in the approved price-list format,
 * from radagent -- no model. The file is saved at once when it reproduces
 * every price; whatever it does not, or leaves out, is shown, never hidden.
 */
function DownloadPriceList({ ns }: { ns: string }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  // Owner 2026-10-29 (D4): each download is kept as a snapshot; billing keeps
  // the newest 5. Best effort: the file is saved whether or not it is kept.
  const [kept, setKept] = useState<string | null>(null);
  const run = useMutation<PriceListExport, Error>({
    mutationFn: () => exportPriceList(identity, ns),
    onSuccess: (x) => {
      if (!x.text) return;
      saveText(x.text, x.filename);
      if (!canKeepPriceListDocs(identity.roles)) return;
      savePriceListDoc(identity, ns, {
        kind: "snapshot", title: `What billing charged on ${x.on}`, filename: x.filename,
        mediaType: MARKDOWN, contentBase64: textToBase64(x.text),
        ...(x.differences.length ? { note: `${x.differences.length} price(s) not as billing charges` } : {}),
      }).then(
        (d) => {
          setKept(`Kept as document #${d.id} (Plans › Price-list documents).`);
          void qc.invalidateQueries({ queryKey: ["pricelistdocs", ns] });
        },
        (e: Error) => setKept(`Not kept as a document: ${e.message}`),
      );
    },
  });
  const x = run.data;
  return (
    <Box sx={{ mb: 2 }}>
      <Button variant="outlined" size="small" disabled={run.isPending} onClick={() => run.mutate()}>
        {run.isPending ? "Preparing…" : "Download current price list"}
      </Button>
      {run.isError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          <AlertTitle>Not downloaded</AlertTitle>
          {run.error.message}
        </Alert>
      )}
      {x && (
        <Alert severity={x.differences.length || !x.text ? "warning" : "success"} sx={{ mt: 1 }}>
          <AlertTitle>
            {!x.text
              ? "The file could not be written"
              : x.differences.length
                ? `Saved ${x.filename}, but it does not match billing in ${x.differences.length} place(s)`
                : `Saved ${x.filename}: every price billing charges on ${x.on}`}
          </AlertTitle>
          {kept && <Typography variant="body2">{kept}</Typography>}
          {x.differences.length > 0 && <DownloadList title="Not as billing charges" items={x.differences} />}
          {x.left_out.length > 0 && <DownloadList title="Not in the file" items={x.left_out} />}
          {x.pending.length > 0 && (
            <DownloadList title="Changes that start later (not in the file yet)"
              items={x.pending.map((p) => `${describeSelector(p.labels)}: ₹${p.amount} net from ${p.from}`)} />
          )}
        </Alert>
      )}
    </Box>
  );
}

function DownloadList({ title, items }: { title: string; items: string[] }) {
  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography variant="body2" fontWeight={600}>{title}</Typography>
      <ul style={{ margin: "2px 0 0 0", paddingLeft: 18 }}>
        {items.map((t) => (
          <li key={t}>
            <Typography variant="body2">{t}</Typography>
          </li>
        ))}
      </ul>
    </Box>
  );
}

function revertWords(res: RevertResult, gst: number, speak = true): string {
  const old = res.predecessor ? rulePays({ pricingmode: "ABSOLUTE", amount: res.predecessor.amount }, gst, speak) : "the previous price";
  if (res.replay) return `This revert was already made. ${old} applies.`;
  if (res.created) return `Reverted: ${old} again from ${istDate(res.created.effectivefrom)}.`;
  return `Reverted: the change is withdrawn and ${old} applies as if it had never been made.`;
}

/**
 * Revert a price change that has started (owner 2026-10-29): from a date on
 * or after its start -- a past date is allowed. On the change's own start
 * day it is withdrawn as if never made; later, it ends that day and the
 * previous price returns. Issued invoices and receipts are not rewritten.
 */
function RevertDialog({ ns, rule, gst, speak = true, onClose }: { ns: string; rule: PriceRule; gst: number; speak?: boolean; onClose: () => void }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const start = istDay(new Date(rule.effectivefrom));
  const [from, setFrom] = useState(start);
  const bad = !/^\d{4}-\d{2}-\d{2}$/.test(from) || from < start;
  const save = useMutation({
    mutationFn: () => revertPriceChange(identity, ns, { ruleid: rule.id, effectivefrom: from }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["pricelist", ns] });
      void qc.invalidateQueries({ queryKey: ["plan-detail-rules", ns] });
    },
  });
  const done = save.data;
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Revert this price change</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {describeSelector(rule.parentlabelselector ?? rule.labelselector)}: {rulePays(rule, gst, speak)} since {istDate(rule.effectivefrom)}
        </Typography>
        {done ? (
          <Alert severity={done.replay ? "info" : "success"}>{revertWords(done, gst, speak)}</Alert>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField size="small" type="date" label="Revert from" value={from} InputLabelProps={{ shrink: true }}
              inputProps={{ min: start }} error={bad}
              helperText={from === start ? "Its start day: withdrawn as if it had never been made." :
                "The change applies until this day; the previous price from it (India time)."}
              onChange={(e) => setFrom(e.target.value)} />
            <Alert severity="info">Invoices and receipts already issued stay as they are.</Alert>
            {save.isError && (
              <Alert severity="error">
                <AlertTitle>Not reverted</AlertTitle>
                {(save.error as Error).message}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!done && (
          <Button variant="contained" color="warning" disabled={bad || save.isPending} onClick={() => save.mutate()}>
            Revert
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/**
 * Move or cancel a price change that has not started (owner 2026-10-28).
 * radbilling refuses one that has started, and a date that is past or before
 * the old price; its refusal is shown as it says it.
 */
function PendingChangeDialog({ ns, rule, gst, speak = true, action, onClose }: {
  ns: string;
  rule: PriceRule;
  gst: number;
  speak?: boolean;
  action: "move" | "cancel";
  onClose: () => void;
}) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const now = new Date();
  const [from, setFrom] = useState(istDay(new Date(rule.effectivefrom)));
  const dateProblem = action === "move" && (!/^\d{4}-\d{2}-\d{2}$/.test(from) || from < istDay(now, 1)) ? "A price changes from tomorrow at the earliest" : "";
  const save = useMutation({
    mutationFn: () => changePendingPrice(identity, ns, { ruleid: rule.id, action, ...(action === "move" ? { effectivefrom: from } : {}) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["pricelist", ns] });
      void qc.invalidateQueries({ queryKey: ["plan-detail-rules", ns] });
    },
  });
  const done = save.data;
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{action === "move" ? "Move the date" : "Cancel this change"}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {describeSelector(rule.parentlabelselector ?? rule.labelselector)}: {rulePays(rule, gst, speak)} from {istDate(rule.effectivefrom)}
        </Typography>
        {done ? (
          <Alert severity={done.replay ? "info" : "success"}>{pendingWords(done, action, gst, speak)}</Alert>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {action === "move" ? (
              <TextField size="small" type="date" label="New start" value={from} InputLabelProps={{ shrink: true }}
                inputProps={{ min: istDay(now, 1) }} error={Boolean(dateProblem)}
                helperText={dateProblem || "Same price, new date (India time). The old price applies until then."}
                onChange={(e) => setFrom(e.target.value)} />
            ) : (
              <Typography variant="body2">The new price never starts; the old price stays, with no end date.</Typography>
            )}
            {save.isError && (
              <Alert severity="error">
                <AlertTitle>Not changed</AlertTitle>
                {(save.error as Error).message}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!done && (
          <Button variant="contained" color={action === "cancel" ? "warning" : "primary"}
            disabled={save.isPending || Boolean(dateProblem)} onClick={() => save.mutate()}>
            {action === "move" ? "Move" : "Cancel the change"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/**
 * Change price (owner 2026-10-28): one price, from a date, no document. The
 * operator states the price WITH GST, as on every other screen; the rule
 * stores the net, computed exactly as the price-list compiler does, and both
 * are shown before saving.
 */
export function ChangePriceDialog({ ns, rule, gst, speak = true, onClose }: { ns: string; rule: PriceRule; gst: number; speak?: boolean; onClose: () => void }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const now = new Date();
  const [gross, setGross] = useState("");
  const [from, setFrom] = useState(istDay(now, 1));
  const [touched, setTouched] = useState(false);
  const problems = repriceProblems(gross, from, now);
  const net = problems.length ? null : netOfGross(Number(gross), gst);
  const save = useMutation({
    mutationFn: () => repricePriceRule(identity, ns, { ruleid: rule.id, amount: net!, effectivefrom: from }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["pricelist", ns] });
      void qc.invalidateQueries({ queryKey: ["plan-detail-rules", ns] });
    },
  });
  const done = save.data;
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Change price</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {describeSelector(rule.parentlabelselector ?? rule.labelselector)}: now {rulePays(rule, gst, speak)}
        </Typography>
        {done ? (
          <Alert severity={done.unchanged || done.replay ? "info" : "success"}>{repriceWords(done, gst, speak)}</Alert>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField size="small" label={!speak ? "New price (₹)" : gst > 0 ? `New price incl. ${gst}% GST (₹)` : "New price (₹, no GST)"} value={gross} autoFocus inputProps={{ inputMode: "numeric" }}
              error={touched && problems.some((x) => x.includes("price"))}
              helperText={net != null && speak ? `Stored as ${rulePays({ pricingmode: "ABSOLUTE", amount: net }, gst)}` : " "}
              onChange={(e) => setGross(e.target.value)} />
            <TextField size="small" type="date" label="From" value={from} InputLabelProps={{ shrink: true }}
              inputProps={{ min: istDay(now, 1) }}
              error={touched && problems.some((x) => x.includes("date") || x.includes("tomorrow"))}
              helperText="The old price applies until this day; the new one from it (India time)."
              onChange={(e) => setFrom(e.target.value)} />
            {touched && problems.length > 0 && <Alert severity="error">{problems.join(". ")}</Alert>}
            {save.isError && (
              <Alert severity="error">
                <AlertTitle>Not changed</AlertTitle>
                {(save.error as Error).message}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{done ? "Close" : "Cancel"}</Button>
        {!done && (
          <Button variant="contained" disabled={save.isPending}
            onClick={() => {
              setTouched(true);
              if (problems.length === 0) save.mutate();
            }}>
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/**
 * Plans › Price list. READ-ONLY: what radbilling holds for this namespace --
 * each billable component, its net price and GST, what a subscriber pays, the
 * plan it is linked to, and the rules that price it.
 *
 * 🔑 ONE WRITE ONLY: Change price (owner 2026-10-28, overriding 2026-09-28
 * design §9.5 D3: billing is now the master copy, a document only an input).
 * It ends a fixed price at a date and starts the new one, so the old price
 * stays in the history and an invoice always shows which applied.
 *
 * An empty list is said in words, because for most operators today it IS empty
 * -- the file-to-rows compiler does not exist yet -- and a blank table reads as
 * a broken screen rather than as the truthful answer.
 */
export function PriceListScreen() {
  const identity = useIdentity();
  const own = identity.namespace ?? "";
  const [target, setTarget] = useState(own);
  const [ns, setNs] = useState(own);
  const [changing, setChanging] = useState<{ rule: PriceRule; gst: number } | null>(null);
  const [pending, setPending] = useState<{ rule: PriceRule; gst: number; action: "move" | "cancel" } | null>(null);
  const [reverting, setReverting] = useState<{ rule: PriceRule; gst: number } | null>(null);
  // DISPLAY ONLY: radaccess plan:write decides (superadmin, bssadmin, Operator).
  const mayChange = canOpenCreatePlan(identity.roles);

  const load = useQuery({
    queryKey: ["pricelist", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: async ({ signal }) => {
      const [components, rules] = await Promise.all([
        listPriceComponents(identity, ns, signal),
        listPriceRules(identity, ns, signal),
      ]);
      return priceListRows(components, rules);
    },
  });
  // Owner 2026-10-29: each line's GST is its seller of record's, as billing's
  // quote states it. A line billing did not state is "—" and cannot be changed
  // here: its net could not be worked out without assuming a rate.
  const sellerGst = useQuery({
    queryKey: ["pricelist-gst", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: ({ signal }) => listSellerGst(identity, ns, signal),
  });
  // Owner 2026-10-29: an unregistered or composition operator sees prices with no word of GST.
  const speak = speaksGst(sellerGst.data);

  // A withdrawn rule charges nobody and is no warning (owner 2026-11-01).
  const openUnattached = (load.data?.unattached ?? []).filter((r) => r.active);
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Price list
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What the platform holds as this provider&apos;s prices.{" "}
        {mayChange ? "Change price ends a price at a date and starts the new one; the history is kept." : "Read-only."}
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <TextField size="small" label="Namespace" value={target} sx={{ minWidth: 320 }}
          helperText="Your own, or one of your sites"
          onChange={(e) => setTarget(e.target.value.trim().toLowerCase())} />
        <Button variant="outlined" sx={{ height: 40 }} onClick={() => setNs(target)}>
          Open
        </Button>
      </Stack>
      {ns && <DownloadPriceList key={ns} ns={ns} />}

      {load.isLoading && <Typography variant="body2">Loading…</Typography>}
      {load.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>{errorTitle(load.error as Error)}</AlertTitle>
          {(load.error as Error).message}
        </Alert>
      )}

      {load.data && load.data.rows.length === 0 && load.data.unattached.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>No prices are recorded for {ns} yet</AlertTitle>
          Prices reach the platform when an approved price list is turned into components and price rules. A test
          namespace can do this from Plans › Propose from a document (Approve); a real operator's price list is
          approved by Ankh.
        </Alert>
      )}

      {load.data && load.data.rows.length > 0 && (
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table size="small" aria-label="Price list">
            <TableHead>
              <TableRow>
                <TableCell>Component</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="right">Price</TableCell>
                {speak && <TableCell align="right">Net</TableCell>}
                {speak && <TableCell align="right">GST</TableCell>}
                <TableCell>HSN/SAC</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Price rules</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {load.data.rows.map(({ component: c, rules }) => {
                const gst = sellerGstOf(c, sellerGst.data);
                return (
                <TableRow key={c.id} sx={c.active ? undefined : { opacity: 0.55 }}>
                  <TableCell>
                    {c.name}
                    {!c.active && <Chip size="small" label="withdrawn" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell>{c.category}</TableCell>
                  <TableCell align="right">
                    {gst === undefined ? "—" : priceWords(sellerGross(c.price, gst), gst, speak)}
                    {speak && sellerWarningOf(c, sellerGst.data) && (
                      <Chip size="small" color="warning" sx={{ ml: 1 }} title={sellerWarningOf(c, sellerGst.data)}
                        label={warningWords(sellerWarningOf(c, sellerGst.data)!)} />
                    )}
                  </TableCell>
                  {speak && <TableCell align="right">{inr(c.price)}</TableCell>}
                  {speak && (
                    <TableCell align="right"
                      title={gst === undefined ? sellerGst.data?.unquoted?.[c.id] ?? (sellerGst.error as Error | null)?.message ?? "billing did not state a rate" : undefined}>
                      {gst === undefined ? (sellerGst.isLoading ? "…" : "—") : `${gst}%`}
                    </TableCell>
                  )}
                  <TableCell>{c.hsnsac ?? ""}</TableCell>
                  <TableCell>
                    {c.radcatalog_groupname || (
                      <Typography variant="caption" color="text.secondary"
                        title="No sellable plan is built from this price yet. Plans › Create plan picks it and builds the plan that subscribers buy.">
                        no plan built yet
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {/* "compiled" = written from an approved price list (catalogref names the file
                        and commit); not a button. Owner 2026-10-11 read it as one. */}
                    <Chip size="small" title={c.catalogref || undefined}
                      label={sourceOf(c) === "compiled" ? "from a price list" : "typed by hand"}
                      color={sourceOf(c) === "compiled" ? "success" : "default"} />
                  </TableCell>
                  <TableCell>
                    {rules.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        none -- the component price applies
                      </Typography>
                    ) : (
                      <RuleTable rules={rules} gst={gst} speak={speak}
                        {...(mayChange && c.active && gst !== undefined
                          ? {
                              onChange: (rule: PriceRule) => setChanging({ rule, gst }),
                              onPending: (rule: PriceRule, action: "move" | "cancel") => setPending({ rule, gst, action }),
                              onRevert: (rule: PriceRule) => setReverting({ rule, gst }),
                            }
                          : {})} />
                    )}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {changing && <ChangePriceDialog ns={ns} rule={changing.rule} gst={changing.gst} speak={speak} onClose={() => setChanging(null)} />}
      {reverting && (
        <RevertDialog key={reverting.rule.id} ns={ns} rule={reverting.rule} gst={reverting.gst} speak={speak} onClose={() => setReverting(null)} />
      )}
      {pending && (
        <PendingChangeDialog key={`${pending.rule.id}-${pending.action}`} ns={ns} rule={pending.rule} gst={pending.gst} speak={speak}
          action={pending.action} onClose={() => setPending(null)} />
      )}

      {openUnattached.length > 0 && (
        <Alert severity="warning">
          <AlertTitle>
            {openUnattached.length} price rule{openUnattached.length === 1 ? "" : "s"} not tied to a component listed here
          </AlertTitle>
          These price by label or by a component in another namespace, so they are shown apart rather than hidden.
          {openUnattached.map((r) => (
            <RuleLine key={r.id} r={r} />
          ))}
        </Alert>
      )}
    </Box>
  );
}
